/**
 * Minimal SDP transformer for ContinuumCX WebRTC client.
 *
 * v2 (2026-06-02): Corrected to preserve BUNDLE consistency.
 *
 * WHY THIS EXISTS:
 *   Modern Chrome offers a kitchen-sink SDP with header extensions, RTCP
 *   feedback, transport-cc, etc. SBCs (Kamailio+rtpengine) and Asterisk
 *   sometimes don't echo every attribute back, which can cause negotiation
 *   issues with media or codec selection. We strip the truly OPTIONAL noise
 *   while leaving everything WebRTC considers mandatory.
 *
 * WHAT WE KEEP (REQUIRED for the call to work):
 *   - All BUNDLE / mid attributes (Chrome enforces consistency in answer)
 *   - DTLS: a=setup, a=fingerprint, a=tls-id
 *   - ICE:  a=ice-ufrag, a=ice-pwd, a=ice-options, a=candidate, a=end-of-candidates
 *   - Media multiplexing: a=rtcp-mux, a=rtcp-rsize, a=rtcp:
 *   - Codecs in our allow-list and their rtpmap/fmtp
 *   - a=msid / a=ssrc (Chrome needs these for stream identification)
 *   - a=sendrecv / direction
 *
 * WHAT WE STRIP (optional/noisy, safe to remove):
 *   - a=extmap:* (RTP header extensions - useful for advanced features
 *     we don't use; some SBCs don't echo them which causes asymmetry)
 *   - a=rtcp-fb:* (RTCP feedback - mostly used for video congestion control)
 *   - a=extmap-allow-mixed (purely an indicator)
 *   - Non-allow-list codecs from m-line and their rtpmap/fmtp
 *
 * Allowed codecs: opus (111), G722 (9), PCMU (0), PCMA (8), telephone-event
 *   PT may vary by browser; we resolve them dynamically from rtpmap.
 *
 * Reference: RFC 8829 (JSEP), RFC 8843 (BUNDLE), RFC 5763 (DTLS-SRTP).
 */

// Codec names we want to keep (case-insensitive).
// We resolve them to dynamic payload types per SDP.
const KEEP_CODECS = new Set([
  'opus',
  'g722',
  'pcmu',
  'pcma',
  'telephone-event',
]);

/**
 * Parse an SDP and return the set of payload-type numbers whose rtpmap
 * name matches our allow-list. Always includes the static PTs 0 (PCMU)
 * and 8 (PCMA) if they appear in the m=audio line.
 */
function findKeepPayloadTypes(sdpText) {
  const keep = new Set();
  const lines = sdpText.split(/\r?\n/);

  for (const line of lines) {
    // a=rtpmap:111 opus/48000/2
    const m = line.match(/^a=rtpmap:(\d+)\s+([^/\s]+)/i);
    if (m) {
      const pt = m[1];
      const name = m[2].toLowerCase();
      if (KEEP_CODECS.has(name)) keep.add(pt);
    }
  }

  // Static payload types we always keep if present
  // 0 = PCMU, 8 = PCMA, 9 = G722
  return keep;
}

/**
 * Filter the m=audio line to only include payload types we keep.
 * If no PTs remain, leave the original line untouched (safety net).
 */
function filterMLine(mline, keepPTs) {
  // m=audio <port> <proto> <pt1> <pt2> ...
  const parts = mline.split(/\s+/);
  if (parts.length < 4) return mline;
  const [m, port, proto, ...pts] = parts;
  const kept = pts.filter((pt) => keepPTs.has(pt));
  if (kept.length === 0) return mline; // never strip all
  return [m, port, proto, ...kept].join(' ');
}

/**
 * Determine whether to drop a line (returns true if drop).
 * Receives a line string AND the set of kept PTs.
 */
function shouldDrop(line, keepPTs) {
  // ------ DROP entire line: noisy optional attributes ------
  if (line.startsWith('a=extmap:')) return true;
  if (line.startsWith('a=extmap-allow-mixed')) return true;
  if (line.startsWith('a=rtcp-fb:')) return true;

  // ------ DROP if it belongs to a payload type we removed ------
  // a=rtpmap:PT ..., a=fmtp:PT ..., a=rtcp-fb:PT ...
  const m = line.match(/^a=(?:rtpmap|fmtp|rtcp-fb):(\d+)/);
  if (m && !keepPTs.has(m[1])) return true;

  return false;
}

/**
 * Repair an incoming SDP answer that came from rtpengine.
 *
 * Why this exists:
 *   rtpengine (mr26.1) sometimes strips the session-level `a=group:BUNDLE 0`
 *   attribute when rewriting an answer SDP. Chrome strictly enforces RFC 8843:
 *   if the offer advertised BUNDLE, the answer MUST contain a matching
 *   `a=group:BUNDLE <mid>` for every kept m-section's `a=mid:` value, or
 *   `setRemoteDescription` fails with:
 *     "Answer cannot remove m= section with mid='X' from already-established
 *      BUNDLE group."
 *
 * What this does:
 *   Scans the SDP for all `a=mid:` values inside m-sections we kept, and if
 *   no `a=group:BUNDLE <those mids>` exists at session-level, inserts one
 *   immediately after the `t=` line.
 */
export function fixRemoteSDP(sdpIn) {
  if (!sdpIn || typeof sdpIn !== 'string') return sdpIn;

  const eol = sdpIn.includes('\r\n') ? '\r\n' : '\n';
  const lines = sdpIn.split(/\r?\n/);

  // Collect all mids in the order they appear.
  const mids = [];
  for (const line of lines) {
    const m = line.match(/^a=mid:(\S+)/);
    if (m) mids.push(m[1]);
  }

  // Check if BUNDLE group already exists with those mids.
  const hasMatchingBundle = lines.some((line) => {
    if (!line.startsWith('a=group:BUNDLE')) return false;
    const parts = line.substring('a=group:BUNDLE'.length).trim().split(/\s+/);
    return mids.length > 0 && mids.every((m) => parts.includes(m));
  });

  if (mids.length === 0) {
    // Nothing to bundle.
    return sdpIn;
  }
  const needsBundle = !hasMatchingBundle;

  // Insert `a=group:BUNDLE <mids>` at session level, right after the t= line.
  // Session-level lines end at the first m= line.
  // Also ensure each m-section has `a=rtcp-mux` — Chrome rejects with
  // "rtcp-mux must be enabled when BUNDLE is enabled" otherwise.
  // Asterisk's hold/unhold re-INVITE answers sometimes omit rtcp-mux even
  // though the original offer had it.
  const bundleLine = `a=group:BUNDLE ${mids.join(' ')}`;
  const out = [];
  let inserted = false;

  // First pass: figure out which m-sections need rtcp-mux added.
  // Track section boundaries (m= lines) and whether each has rtcp-mux.
  const sectionStarts = [];
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('m=')) sectionStarts.push(i);
  }
  const sectionNeedsMux = sectionStarts.map((startIdx, sIdx) => {
    const endIdx = sIdx + 1 < sectionStarts.length ? sectionStarts[sIdx + 1] : lines.length;
    for (let j = startIdx; j < endIdx; j++) {
      if (lines[j].trim() === 'a=rtcp-mux') return false;
    }
    return true;
  });
  const muxAddedFor = [];

  let curSection = -1;
  let muxInsertedThisSection = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (needsBundle && !inserted && line.startsWith('m=')) {
      out.push(bundleLine);
      inserted = true;
    }
    if (line.startsWith('m=')) {
      curSection += 1;
      muxInsertedThisSection = false;
    }
    out.push(line);
    // Insert a=rtcp-mux right after the m= line of any section missing it.
    if (
      curSection >= 0 &&
      sectionNeedsMux[curSection] &&
      !muxInsertedThisSection &&
      line.startsWith('m=')
    ) {
      out.push('a=rtcp-mux');
      muxInsertedThisSection = true;
      muxAddedFor.push(mids[curSection] ?? `#${curSection}`);
    }
  }
  // Defensive: if BUNDLE was missing and there were no m= sections, append at end.
  if (needsBundle && !inserted) out.push(bundleLine);

  // If nothing changed (BUNDLE already present, no rtcp-mux to add), return original.
  if (!needsBundle && muxAddedFor.length === 0) {
    return sdpIn;
  }

  let result = out.join(eol);
  if (!result.endsWith(eol)) result += eol;
  // eslint-disable-next-line no-console
  console.log('[CX] SDP fix:',
    needsBundle ? `inserted BUNDLE for mids ${mids.join(', ')}` : '',
    muxAddedFor.length ? `added rtcp-mux to sections: ${muxAddedFor.join(', ')}` : '');
  return result;
}

export function minifySDP(sdpIn) {
  if (!sdpIn || typeof sdpIn !== 'string') return sdpIn;

  const eol = sdpIn.includes('\r\n') ? '\r\n' : '\n';
  const lines = sdpIn.split(/\r?\n/);

  // Step 1: figure out which payload types to keep
  const keepPTs = findKeepPayloadTypes(sdpIn);
  if (keepPTs.size === 0) {
    // Defensive: if no allow-list matches, leave SDP as-is.
    return sdpIn;
  }

  // Step 2: walk lines, filter m-line, drop noise
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line === '') {
      // Preserve empty trailing line(s) for proper SDP termination
      out.push(line);
      continue;
    }
    if (line.startsWith('m=audio')) {
      out.push(filterMLine(line, keepPTs));
      continue;
    }
    if (shouldDrop(line, keepPTs)) continue;
    out.push(line);
  }

  let result = out.join(eol);
  // SDP must end with EOL
  if (!result.endsWith(eol)) result += eol;
  return result;
}

/**
 * Install the minifier on a JsSIP RTCSession. Only mutates LOCAL outgoing
 * SDP (our offer or our answer). We do NOT touch incoming SDP, since
 * Chrome must see exactly what the other peer sent.
 */
export function installSDPMinifier(session) {
  if (!session || !session.on) return;
  session.on('sdp', (e) => {
    try {
      if (e.originator === 'local') {
        // Our outgoing offer/answer: strip optional noise.
        const before = e.sdp;
        const after = minifySDP(before);
        e.sdp = after;
        // eslint-disable-next-line no-console
        console.log('[CX] SDP minified (outgoing)', {
          beforeLines: before.split('\n').length,
          afterLines: after.split('\n').length,
        });
      } else if (e.originator === 'remote') {
        // Incoming offer/answer from the SBC: repair BUNDLE that rtpengine
        // sometimes strips. We never touch ICE/DTLS/codecs here.
        const before = e.sdp;
        const after = fixRemoteSDP(before);
        if (after !== before) {
          e.sdp = after;
          // eslint-disable-next-line no-console
          console.log('[CX] Remote SDP repaired before setRemoteDescription');
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[CX] SDP transform failed, using original', err);
    }
  });
}

export default minifySDP;
